import { Earth, EarthLock, Link, Trash2Icon } from 'lucide-react';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { OptionItem } from '@/components/inspector/entity-fields/form/option-item';
import { useDeleteShareMutation } from '@/hooks/shares/useDeleteShareMutation';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { shareHasDownloadableContent } from '@/utilities/share';
import { useClipboard } from '@/hooks/useClipboard';
import { Badge } from '@/components/ui/badge';
import { type Share } from '@/types/share';
import { Variants } from '@/types/asset';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const ShareOptionItem = ({
  option,
  onClick,
  lastShare,
  readOnly,
}: {
  option: Share;
  onClick: () => void;
  lastShare: boolean;
  readOnly: boolean;
}) => {
  const { copy } = useClipboard();

  const openModal = useOpenModal();
  const baseShareHref = `${window.location.origin}/shares/${option.id}`;
  const hasCustomPreviewVariant = option.previewVariant && option.previewVariant.slug !== Variants.PreviewInternal;
  const hasExtraVariants = option.extraVariants && option.extraVariants.length > 0;
  const hasOriginalFile = Boolean(option.includeOriginalFile);
  const isDownloadable = shareHasDownloadableContent(option);

  const tCommon = useTranslations('common');
  const tShares = useTranslations('shares');

  const { deleteShareMutation } = useDeleteShareMutation();

  const share_otp_guest_login = useFeatureStore((s) => s.share_otp_guest_login);
  const share_collaboration = useFeatureStore((s) => s.share_collaboration);

  const userCount = share_otp_guest_login ? (option.sharedUsers?.length ?? 0) : (option.sharedWith?.length ?? 0);
  const hasUsers = userCount > 0;

  return (
    <OptionItem optionKey={option.id} className="border-b border-ui-base py-3">
      <div className="flex w-full items-start gap-2">
        <div className="flex shrink-0 items-start pt-0.5 text-text-gray">
          {option.isPublic ? <Earth className="icon-5" /> : <EarthLock className="icon-5" />}
        </div>
        <div className={cn('flex grow flex-col gap-1', !readOnly && 'cursor-pointer')} onClick={onClick}>
          <div className="flex flex-wrap gap-1">
            <Badge
              variant={hasUsers ? 'warning' : 'dark'}
              value={hasUsers ? tShares('userCount', { count: userCount }) : tShares('none')}
            >
              {tShares('privateAccess')}
            </Badge>
            {share_otp_guest_login && (
              <Badge
                variant={option.isPublic ? 'sharing' : 'dark'}
                value={option.isPublic ? tShares('on') : tShares('off')}
              >
                {tShares('publicLink')}
              </Badge>
            )}
            <Badge
              variant={isDownloadable ? 'highlight' : 'dark'}
              value={isDownloadable ? tShares('on') : tShares('off')}
            >
              {tShares('downloadable')}
            </Badge>
            {share_collaboration && (
              <Badge
                variant={option.collaborationEnabled ? 'album' : 'dark'}
                value={option.collaborationEnabled ? tShares('on') : tShares('off')}
              >
                {tShares('collaboration')}
              </Badge>
            )}
          </div>

          {(hasCustomPreviewVariant || hasExtraVariants || hasOriginalFile) && (
            <div className="flex flex-wrap items-center gap-1">
              {hasCustomPreviewVariant && (
                <Badge key={option.previewVariant?.id} variant="light" className="pointer-events-none wrap-anywhere">
                  {option.previewVariant?.name}
                </Badge>
              )}
              {option.extraVariants?.map((variant) => (
                <Badge key={variant.id} className="pointer-events-none wrap-anywhere" variant="base">
                  {variant.name}
                </Badge>
              ))}
              {hasOriginalFile && (
                <Badge className="pointer-events-none" variant="base">
                  {tShares('original')}
                </Badge>
              )}
            </div>
          )}

          <div className="flex gap-x-1 text-xs text-text-base">
            <span className="font-bold">{tShares('expires')}:</span> {option.expiresAt ?? tShares('never')}
          </div>
        </div>
        <div className="flex gap-1">
          <OptionAction
            onClick={() => copy(baseShareHref)}
            icon={Link}
            data-cy="option-action-button-share-copy-link"
            tooltip={tCommon('copyLink')}
          />
          {!readOnly && (
            <OptionAction
              onClick={() =>
                openModal(ModalId.DeleteConfirmationAlertDialog, {
                  'content': `Are you sure you want to delete this share ${option.id}?`,
                  'onClick': () => {
                    if (option.id && option.nodeId) {
                      deleteShareMutation.mutate({
                        nodeId: option.nodeId,
                        shareId: option.id,
                        lastShare: lastShare,
                      });
                    }
                  },
                  'data-cy': 'delete-share',
                })
              }
              icon={Trash2Icon}
              variant="destructive"
              data-cy="option-action-button-share-delete"
              tooltip={tCommon('delete')}
            />
          )}
        </div>
      </div>
    </OptionItem>
  );
};
